// Season report card — the GM Score.
//
// A pure, deterministic scoring of a single team-season across six categories
// plus a flat championship bonus. It is intentionally decoupled from the
// database: the worker assembles a ReportCardInput from existing stores
// (teamSeason, awards, player.ratings, draftLotteryResults, the apron ledger)
// and this module turns it into a shareable GM Score, letter grade, and
// category breakdown.
//
// Design principle (see research/season-report-card-spec.md): grade partly
// against a preseason expectation so a rebuilding team that beats its Vegas-
// style over/under can out-score a talented team that underperforms. Absolute
// achievement still dominates — a championship carries a flat bonus larger than
// the entire "process" ceiling, so a title always tops the leaderboard.

// ---------------------------------------------------------------------------
// Inputs / outputs
// ---------------------------------------------------------------------------

export type ReportCardAward =
	| "mvp"
	| "finalsMvp"
	| "dpoy"
	| "roy"
	| "mip"
	| "smoy"
	| "allLeague1"
	| "allLeague2"
	| "allLeague3"
	| "allDefensive"
	| "allStar";

export type ReportCardInput = {
	// --- League context ---
	// Number of playoff rounds in this league (numGamesPlayoffSeries.length).
	// A champion has playoffRoundsWon === numPlayoffRounds.
	numPlayoffRounds: number;

	// --- Results ---
	won: number;
	lost: number;
	// Regular-season point differential per game (ZenGM's team "mov" stat).
	avgMargin: number;

	// --- Playoffs ---
	// -1 = missed; 0 = made but lost round 1; k = won k rounds;
	// numPlayoffRounds = champion.
	playoffRoundsWon: number;
	// Reached the play-in but did not make the bracket (playoffRoundsWon < 0).
	madePlayIn?: boolean;

	// --- Expectations (Vegas over/under) ---
	projectedWins: number;

	// --- Development ---
	// One entry per young player (age <= devAgeMax at season end): the change in
	// overall rating across the season (rookies use draft-day ovr as baseline).
	// Positive and negative values both allowed; only positives score.
	youngPlayerOvrDeltas: number[];

	// --- Accolades ---
	// Every award won by a roster player this season (repeat types allowed, e.g.
	// two All-Stars => ["allStar", "allStar"]).
	awards: ReportCardAward[];

	// --- Asset & cap ---
	// 1-based lottery pick landed this year, or null if the team had none.
	lotteryPick: number | null;
	// Payroll under the luxury-tax line at end of season.
	underTax: boolean;
	// Paid the luxury tax the previous season (used to reward "escaping" it).
	paidTaxLastSeason: boolean;
	// Was over an apron previously and is now under it.
	escapedApron?: boolean;
};

export type ReportCardCategoryKey =
	| "results"
	| "playoffs"
	| "expectations"
	| "development"
	| "accolades"
	| "rebuild"
	| "capManagement"
	| "championship";

export type ReportCardCategory = {
	key: ReportCardCategoryKey;
	label: string;
	points: number;
	// Nominal bounds for rendering bars. min is 0 except for expectations.
	min: number;
	max: number;
};

export type ReportCard = {
	gmScore: number;
	grade: string;
	champion: boolean;
	categories: ReportCardCategory[];
};

// ---------------------------------------------------------------------------
// Tunable configuration (see spec for rationale)
// ---------------------------------------------------------------------------

export const REPORT_CARD_CONFIG = {
	results: {
		winMax: 18,
		winPctFloor: 0.2, // <= 20% win => 0 win points
		winPctCeil: 0.75, // >= 75% win => full win points
		srsMax: 7,
		srsMarginSpan: 8, // +/- points-per-game that maps to full/zero srs
	},
	playoffs: {
		max: 25,
		playInBump: 2, // for reaching the play-in without making the bracket
	},
	expectations: {
		pointsPerWin: 2,
		min: -10,
		max: 20,
		// Landing a pick this good means the losing was purposeful, not
		// underperformance: the penalty is waived (floored at 0) when a team lands
		// a pick this high or better. Aligned with the floored lottery picks (1-5).
		highPickThreshold: 5,
	},
	development: {
		perOvrPoint: 0.8,
		softMax: 16, // cap on the summed-delta component
		breakoutThreshold: 7, // ovr gain that counts as a breakout
		breakoutBonus: 2,
		breakoutBonusMax: 4,
		get max() {
			return this.softMax + this.breakoutBonusMax; // 20
		},
	},
	accolades: {
		max: 15,
		weights: {
			mvp: 6,
			finalsMvp: 5,
			dpoy: 4,
			roy: 3,
			mip: 3,
			smoy: 2,
			allLeague1: 4,
			allLeague2: 3,
			allLeague3: 2,
			allDefensive: 2,
			allStar: 1.5,
		} satisfies Record<ReportCardAward, number>,
	},
	// The tank payoff. A high lottery pick is a marquee rebuild outcome, scored
	// as a flat reward by pick — landing the pick IS the win, no strings attached
	// (see the #1-pick score floor below). Development is not a gate here; it
	// stacks via its own category and is what lifts a #1-pick team above the
	// floor toward a full B. Only lottery teams score here (contenders have no
	// pick), so it never threatens the top of the board.
	rebuild: {
		// Flat points by 1-based lottery pick landed. Modest — the tiered lottery
		// score floors (below) are what GUARANTEE the grade for a high pick; these
		// additive points only differentiate on the leaderboard and give a genuinely
		// good team that also owns a high pick a small bump above its floor. Kept
		// small so owning a high pick can't inflate an otherwise-strong season past
		// its deserved grade (calibrated on a full simulated season).
		lotteryPoints: [12, 9, 7, 5, 3, 2, 1, 1, 1, 1] as number[],
		get max() {
			return this.lotteryPoints[0] ?? 0; // 12
		},
	},
	// Front-office cap work: rewards ducking the luxury tax and, especially,
	// escaping it after paying (the transactions the user cares about).
	capManagement: {
		max: 9,
		underTaxBonus: 2,
		escapedTaxBonus: 5, // additional, on top of underTaxBonus
		escapedApronBonus: 2,
	},
	// Flat bonus for a title. Sized so a champion clears the A+ line off the
	// championship + playoff points alone (25 playoff + 60 = 85), i.e. a ring is
	// worth more than the entire realistic non-champ ceiling.
	championshipBonus: 60,
	// Score floors (applied as a minimum GM Score, which then derives the grade).
	// A championship guarantees A+. Landing a high lottery pick guarantees a
	// grade no matter the record or development — development/wins push a team
	// ABOVE its floor, but can never drag it below. Floors by 1-based pick:
	//   #1 -> B+ (74), #2 -> B (66), #3 -> B- (58), #4 & #5 -> C (42).
	// Picks outside this list are not floored.
	championFloorScore: 100,
	lotteryFloorScores: [74, 66, 58, 42, 42] as number[],
	// GM Score thresholds, highest first. First threshold met wins. Provisional;
	// retuned against real sim seasons in Phase 3.
	grades: [
		[100, "A+"],
		[90, "A"],
		[82, "A-"],
		[74, "B+"],
		[66, "B"],
		[58, "B-"],
		[50, "C+"],
		[42, "C"],
		[34, "C-"],
		[26, "D"],
		[-Infinity, "F"],
	] as [number, string][],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const clamp = (x: number, lo: number, hi: number) =>
	Math.max(lo, Math.min(hi, x));

// Round to 1 decimal to keep scores tidy and stable for sharing/leaderboards.
const round1 = (x: number) => Math.round(x * 10) / 10;

// ---------------------------------------------------------------------------
// Category scorers
// ---------------------------------------------------------------------------

const scoreResults = (input: ReportCardInput): number => {
	const c = REPORT_CARD_CONFIG.results;
	const games = input.won + input.lost;
	const winPct = games > 0 ? input.won / games : 0;
	const win =
		c.winMax *
		clamp((winPct - c.winPctFloor) / (c.winPctCeil - c.winPctFloor), 0, 1);

	const srs =
		c.srsMax *
		clamp((input.avgMargin + c.srsMarginSpan) / (2 * c.srsMarginSpan), 0, 1);

	return win + srs;
};

const scorePlayoffs = (input: ReportCardInput): number => {
	const c = REPORT_CARD_CONFIG.playoffs;
	if (input.playoffRoundsWon < 0) {
		return input.madePlayIn ? c.playInBump : 0;
	}
	// Linear ladder that generalizes across league round settings:
	// missed -> 0, champion -> max.
	return (
		(c.max * (input.playoffRoundsWon + 1)) / (input.numPlayoffRounds + 1)
	);
};

const scoreExpectations = (input: ReportCardInput): number => {
	const c = REPORT_CARD_CONFIG.expectations;
	const raw = clamp(
		(input.won - input.projectedWins) * c.pointsPerWin,
		c.min,
		c.max,
	);
	// Two things reframe a below-projection record so the penalty is waived
	// (floored at 0): landing a high pick (losing was purposeful), and winning a
	// playoff series (the team proved it was better than its regular-season
	// record — don't punish a deep run for an underwhelming regular season).
	const landedHighPick =
		input.lotteryPick !== null && input.lotteryPick <= c.highPickThreshold;
	const wonPlayoffSeries = input.playoffRoundsWon >= 1;
	return landedHighPick || wonPlayoffSeries ? Math.max(raw, 0) : raw;
};

const scoreDevelopment = (input: ReportCardInput): number => {
	const c = REPORT_CARD_CONFIG.development;
	let positiveSum = 0;
	let breakouts = 0;
	for (const delta of input.youngPlayerOvrDeltas) {
		if (delta > 0) {
			positiveSum += delta;
		}
		if (delta >= c.breakoutThreshold) {
			breakouts += 1;
		}
	}
	const base = clamp(positiveSum * c.perOvrPoint, 0, c.softMax);
	const breakoutBonus = Math.min(
		breakouts * c.breakoutBonus,
		c.breakoutBonusMax,
	);
	return base + breakoutBonus;
};

const scoreAccolades = (input: ReportCardInput): number => {
	const c = REPORT_CARD_CONFIG.accolades;
	let total = 0;
	for (const award of input.awards) {
		total += c.weights[award] ?? 0;
	}
	return Math.min(total, c.max);
};

// The tank payoff: a flat reward by lottery pick landed. Landing the pick is
// the win; development stacks separately via its own category.
const scoreRebuild = (input: ReportCardInput): number => {
	const c = REPORT_CARD_CONFIG.rebuild;
	if (input.lotteryPick === null || input.lotteryPick < 1) {
		return 0;
	}
	return c.lotteryPoints[input.lotteryPick - 1] ?? 0;
};

const scoreCapManagement = (input: ReportCardInput): number => {
	const c = REPORT_CARD_CONFIG.capManagement;
	let total = 0;
	if (input.underTax) {
		total += c.underTaxBonus;
		if (input.paidTaxLastSeason) {
			total += c.escapedTaxBonus;
		}
	}
	if (input.escapedApron) {
		total += c.escapedApronBonus;
	}
	return Math.min(total, c.max);
};

const isChampion = (input: ReportCardInput): boolean =>
	input.playoffRoundsWon === input.numPlayoffRounds &&
	input.numPlayoffRounds > 0;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const gradeForScore = (gmScore: number): string => {
	for (const [threshold, grade] of REPORT_CARD_CONFIG.grades) {
		if (gmScore >= threshold) {
			return grade;
		}
	}
	return "F";
};

export const computeReportCard = (input: ReportCardInput): ReportCard => {
	const champion = isChampion(input);

	const results = scoreResults(input);
	const playoffs = scorePlayoffs(input);
	const expectations = scoreExpectations(input);
	const development = scoreDevelopment(input);
	const accolades = scoreAccolades(input);
	const rebuild = scoreRebuild(input);
	const capManagement = scoreCapManagement(input);
	const championship = champion ? REPORT_CARD_CONFIG.championshipBonus : 0;

	const rawScore =
		results +
		playoffs +
		expectations +
		development +
		accolades +
		rebuild +
		capManagement +
		championship;

	// Guaranteed floors, applied to the score (which then derives the grade):
	// a title guarantees A+; landing a high lottery pick guarantees a tiered
	// grade (#1 B+, #2 B, #3 B-, #4/#5 C). The higher floor wins, and a strong
	// raw score can always exceed either.
	let floorScore = -Infinity;
	if (champion) {
		floorScore = REPORT_CARD_CONFIG.championFloorScore;
	} else if (input.lotteryPick !== null && input.lotteryPick >= 1) {
		floorScore =
			REPORT_CARD_CONFIG.lotteryFloorScores[input.lotteryPick - 1] ?? -Infinity;
	}

	// Never show a negative GM Score on the leaderboard; the worst season is 0.
	const gmScore = round1(Math.max(rawScore, floorScore, 0));

	const categories: ReportCardCategory[] = [
		{
			key: "results",
			label: "Results",
			points: round1(results),
			min: 0,
			max: REPORT_CARD_CONFIG.results.winMax + REPORT_CARD_CONFIG.results.srsMax,
		},
		{
			key: "playoffs",
			label: "Playoffs",
			points: round1(playoffs),
			min: 0,
			max: REPORT_CARD_CONFIG.playoffs.max,
		},
		{
			key: "expectations",
			label: "Expectations",
			points: round1(expectations),
			min: REPORT_CARD_CONFIG.expectations.min,
			max: REPORT_CARD_CONFIG.expectations.max,
		},
		{
			key: "development",
			label: "Development",
			points: round1(development),
			min: 0,
			max: REPORT_CARD_CONFIG.development.max,
		},
		{
			key: "accolades",
			label: "Accolades",
			points: round1(accolades),
			min: 0,
			max: REPORT_CARD_CONFIG.accolades.max,
		},
		{
			key: "rebuild",
			label: "Rebuild",
			points: round1(rebuild),
			min: 0,
			max: REPORT_CARD_CONFIG.rebuild.max,
		},
		{
			key: "capManagement",
			label: "Cap Management",
			points: round1(capManagement),
			min: 0,
			max: REPORT_CARD_CONFIG.capManagement.max,
		},
	];

	if (championship > 0) {
		categories.push({
			key: "championship",
			label: "Championship",
			points: championship,
			min: 0,
			max: REPORT_CARD_CONFIG.championshipBonus,
		});
	}

	// Grade derives from the (already floored) score, so the championship A+ and
	// the #1-pick "win" (B-) guarantees fall out automatically.
	return {
		gmScore,
		grade: gradeForScore(gmScore),
		champion,
		categories,
	};
};
